// The closed sets of core.md §5.3, byte-for-byte with the BFF's
// `src/lib/vocabulary.js` and the store's `casework.py`, plus the catalogue key
// each value renders under.
//
// ── Two kinds of mapper, deliberately ─────────────────────────────────────
//
// The sets this APP owns (`level`, `sex`, `kind`, `status`, …) get TOTAL mappers:
// anything unrecognised folds onto the store's own default, so a catalogue key
// always resolves and a badge never prints a raw dotted string.
//
// The vocabularies that come out of the AI service's rule engine (`severity`,
// `warningType`, `source`) and the model's own self-assessment (`confidence`,
// `likelihood`) get PARTIAL mappers that return null for an unknown value. Those
// outputs are forwarded verbatim by the BFF (core.md §8.4) and folding an unknown
// `severity` onto «متوسط» would be a rewrite of a safety warning, done in the
// browser. The panels print the raw value when the mapper answers null.
//
// tests/i18n.test.js walks every set through its mapper and asserts the key
// exists in both catalogues.

const total = (set, prefix, fallback) => (value) =>
  `${prefix}.${set.includes(value) ? value : fallback}`;
const partial = (set, prefix) => (value) => (set.includes(value) ? `${prefix}.${value}` : null);

// ── The store's closed sets ─────────────────────────────────────────────

export const SECTIONS = ['chief_complaint', 'hpi', 'pmh', 'medications', 'allergies', 'vitals', 'labs'];
export const SEXES = ['male', 'female', 'other'];
export const CASE_STATUSES = ['draft', 'active', 'archived'];
export const ENTRY_SOURCES = ['manual', 'voice', 'ai'];
export const CHAT_ROLES = ['student', 'assistant'];
export const CHAT_KINDS = ['chat', 'differential', 'interaction', 'soap_suggest', 'order_suggest'];
export const LEVELS = ['student', 'intern', 'resident'];
export const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
export const ENCOUNTER_STATUSES = ['open', 'closed'];
export const DX_KINDS = ['primary', 'secondary', 'differential'];
export const DX_STATUSES = ['active', 'resolved', 'ruled_out'];
export const RX_ROUTES = ['oral', 'im', 'iv', 'sc', 'topical', 'inhaled', 'rectal', 'ophthalmic', 'otic', 'nasal', 'sublingual', 'other'];
export const ORDER_TYPES = ['lab', 'imaging', 'procedure', 'referral', 'other'];
export const ORDER_URGENCIES = ['routine', 'urgent', 'stat'];
export const ORDER_STATUSES = ['ordered', 'resulted', 'cancelled'];
export const ORIGINS = ['manual', 'ai'];

/** The timeline event types the BFF derives (core.md §6). */
export const TIMELINE_TYPES = ['encounter_start', 'encounter_end', 'entry', 'diagnosis', 'rx', 'order', 'order_result', 'thread'];

// ── Total mappers (the app's own vocabulary) ─────────────────────────────

export const sexKey = partial(SEXES, 'common.gender');
export const caseStatusKey = total(CASE_STATUSES, 'patients.status', 'active');
export const sourceKey = total(ENTRY_SOURCES, 'history.entry.source', 'manual');
export const levelKey = total(LEVELS, 'onboarding.level', 'student');
export const encounterStatusKey = total(ENCOUNTER_STATUSES, 'visit.status', 'open');
export const dxKindKey = total(DX_KINDS, 'clinical.dx.kind', 'primary');
export const dxStatusKey = total(DX_STATUSES, 'clinical.dx.status', 'active');
export const rxRouteKey = total(RX_ROUTES, 'clinical.rx.route', 'other');
export const orderTypeKey = total(ORDER_TYPES, 'clinical.order.type', 'other');
export const urgencyKey = total(ORDER_URGENCIES, 'clinical.order.urgency', 'routine');
export const orderStatusKey = total(ORDER_STATUSES, 'clinical.order.status', 'ordered');
export const originKey = total(ORIGINS, 'clinical.origin', 'manual');
export const chatKindKey = total(CHAT_KINDS, 'chat.kind', 'chat');
export const timelineTypeKey = total(TIMELINE_TYPES, 'timeline.type', 'entry');

/** The section label keys — camelCased ids, as `history.sections.*` names them. */
export const sectionKey = (id) =>
  `history.sections.${SECTIONS.includes(id) ? id.replace(/_(\w)/g, (_, c) => c.toUpperCase()) : 'chiefComplaint'}`;

// ── Partial mappers (the AI's vocabulary — never coerced) ─────────────────

export const CONFIDENCE_LEVELS = ['low', 'medium', 'high'];
export const confidenceKey = partial(CONFIDENCE_LEVELS, 'chat.confidence');

export const LIKELIHOODS = ['high', 'medium', 'low'];
export const likelihoodKey = partial(LIKELIHOODS, 'clinical.differential.likelihood');

export const SEVERITIES = ['severe', 'moderate', 'mild'];
export const severityKey = partial(SEVERITIES, 'clinical.interaction.severity');

export const WARNING_TYPES = ['interaction', 'contraindication', 'allergy', 'black_box'];
export const warningTypeKey = partial(WARNING_TYPES, 'clinical.interaction.type');

export const WARNING_SOURCES = [
  'rule_engine_static', 'rule_engine_rxnorm', 'rule_engine_contraindication',
  'rule_engine_allergy', 'rule_engine_openfda', 'llm',
];
export const warningSourceKey = partial(WARNING_SOURCES, 'clinical.interaction.source');

// ── Badge variants (the kit's Badge vocabulary, derived — never a new palette) ──

export const caseStatusVariant = (status) =>
  (status === 'archived' ? 'outline' : status === 'draft' ? 'warning' : 'success');
export const severityVariant = (value) =>
  (value === 'severe' ? 'destructive' : value === 'moderate' ? 'warning' : 'outline');
export const likelihoodVariant = (value) =>
  (value === 'high' ? 'destructive' : value === 'medium' ? 'warning' : 'outline');
export const dxStatusVariant = (value) =>
  (value === 'active' ? 'success' : value === 'ruled_out' ? 'outline' : 'warning');
export const orderStatusVariant = (value) =>
  (value === 'resulted' ? 'success' : value === 'cancelled' ? 'outline' : 'warning');
export const urgencyVariant = (value) =>
  (value === 'stat' ? 'destructive' : value === 'urgent' ? 'warning' : 'outline');

/**
 * Warnings, most severe first. A stable sort over the known order, with anything
 * unrecognised kept at the END rather than dropped: an unknown severity is still
 * a warning, and hiding it is the one failure mode a safety list must not have.
 */
export function sortWarnings(warnings) {
  const rank = (w) => {
    const i = SEVERITIES.indexOf(w?.severity);
    return i === -1 ? SEVERITIES.length : i;
  };
  return [...(warnings || [])].sort((a, b) => rank(a) - rank(b));
}
